// Copyright (c) 2021-2024 Broadcom. All Rights Reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
// and/or its subsidiaries.

import { Injectable } from "@angular/core";
import { Dispatcher } from "@app/services/dispatcher/dispatcher";
import { ErrorOccurredEvent } from "@app/services/dispatcher/events/error-occurred";
import { OperationCompletedEvent, OperationInitEvent } from "@app/services/dispatcher/events/operation-events";
import { Subject } from "rxjs";
import { NotificationUtils, TaskNotificationStrings } from "@shared/utils/notification-utils";
import { L10nService } from "@vmw/ngx-vip";
import { TasksV2SubTypesEnum, TasksV2TypesEnum, TaskV2 } from "@api/generated/vcdr";
import { AppConfigService } from "@app/services/app-config/app-config.service";
import { ConsoleType } from "@shared/models/global.model";
import { CspHeaderNotificationConfig } from "@vmw/csp-ngx-components/header";
import { OPERATOR_CONSOLE } from "@op-console/op-console-routing-util";
import { Router } from "@angular/router";

const SEE_TASKS_BUTTON: string = "SeeTasksButton";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class NotificationService {

    private notifications: Subject<CspHeaderNotificationConfig> = new Subject<CspHeaderNotificationConfig>();

    constructor(private dispatcher: Dispatcher,
                private l10nService: L10nService,
                private appConfig: AppConfigService,
                public router: Router) {
        this.dispatcher.onError.subscribe(error => this.error(error));
        this.dispatcher.onOperationCompleted.subscribe(result => this.operationCompleted(result));
        this.dispatcher.onOperationInit.subscribe(initOp => this.operationInit(initOp));
    }

    public getNotifications(): Subject<CspHeaderNotificationConfig> {
        return this.notifications;
    }

    private error(error: ErrorOccurredEvent) {
        // TODO: Remove UI notifications for tasks when backend supports all task types,
        // leave UI notifications for http errors.
        // Show task notifications only for VCDR and Storage console
        if (error.task &&
            this.appConfig.clientType !== ConsoleType.OPERATOR &&
            this.showUINotification(error.task)) {
            this.notifications.next(
                NotificationUtils.createFailedTaskNotification(error.task, error.isNew, this.l10nService));
        } else if (error.httpError) {
            // console.error("Error notification %o", error.httpError);
            const title: string = this.l10nService.getMessage("vlr.console.notifications.error.title",
                                                              [error.httpError.statusText, error.httpError.status]);

            let detail: string;
            // Hide error details for all, except Operator Console
            if (this.appConfig.clientType === ConsoleType.OPERATOR) {
                if (error.httpError.error?.errormessage) {
                    detail = error.httpError.error.errormessage;
                } else {
                    detail = error.httpError.message;
                }
            }

            this.notifications.next(NotificationUtils.createErrorNotification(title, detail));
        }
    }

    private operationCompleted(result: OperationCompletedEvent) {
        // Do not show success notifications for operator console.
        if (this.appConfig.clientType === ConsoleType.OPERATOR) {
            return;
        }
        // TODO: Remove UI notifications for tasks when backend supports all task types
        if (result.task && this.showUINotification(result.task)) {
            this.notifications.next(
                NotificationUtils.createSuccessfulTaskNotification(result.task, result.isNew, this.l10nService));
        } else if (result.eventTitle) {
            const title = this.l10nService.getMessage(result.eventTitle);
            this.notifications.next(NotificationUtils.createSuccessNotification(title, ""));
        }
    }

    private operationInit(initOp: OperationInitEvent): void {
        if (!initOp.tasks || !initOp.tasks.length || !this.showUINotification(initOp.tasks[0])) {
            return;
        }

        const taskStrings: TaskNotificationStrings = NotificationUtils.getStrings(initOp.tasks[0]);
        if (!taskStrings) {
            console.error("No strings found for task: " + initOp.tasks[0]);
            return;
        }

        const resourceId: string = NotificationUtils.getResourceId(initOp.tasks[0]);
        const notificationButtonLabel = this.l10nService.getMessage("console.notifications.failure.button.task");

        if (initOp.failedToSubmit > 0) {
            const title = this.l10nService.getMessage(
                initOp.submitted > 0 ? taskStrings.errorTitlePartial : taskStrings.errorTitle
            );
            let buttonTitle: any;
            let buttonAction: any;
            let description: any;

            if (initOp.submitted > 0) {
                description = this.l10nService.getMessage(taskStrings.errorDescriptionSome, [
                    initOp.submitted,
                    initOp.failedToSubmit,
                ]);
                buttonTitle = notificationButtonLabel;
                buttonAction = SEE_TASKS_BUTTON;
            } else if (initOp.failedToSubmit > 1) {
                description = this.l10nService.getMessage(taskStrings.errorDescriptionAll, [
                    initOp.failedToSubmit,
                ]);
            } else if (resourceId) {
                description = this.l10nService.getMessage(taskStrings.errorDescriptionOne, [resourceId]);
            } else {
                description = this.l10nService.getMessage(taskStrings.errorDescriptionUnknown);
            }

            this.notifications.next(
                NotificationUtils.createErrorNotification(title, description, buttonTitle, buttonAction)
            );
        } else if (initOp.submitted > 0) {
            const title = this.l10nService.getMessage(taskStrings.successTitle);
            let description;
            if (initOp.submitted > 1) {
                description = this.l10nService.getMessage(taskStrings.successDescriptionAll, [
                    initOp.submitted,
                ]);
            } else if (resourceId) {
                description = this.l10nService.getMessage(taskStrings.successDescriptionOne, [resourceId]);
            } else {
                description = this.l10nService.getMessage(taskStrings.successDescriptionUnknown);
            }

            this.notifications.next(
                NotificationUtils.createSuccessNotification(title, description, notificationButtonLabel, SEE_TASKS_BUTTON)
            );
        }
    }

    // Show UI notifications only for tasks which have no backend notifications.
    private showUINotification(task: TaskV2): boolean {
        // Tasks with no backend notifications
        return task.type === TasksV2TypesEnum.DEPLOYMENT && task.sub_type === TasksV2SubTypesEnum.UPGRADE;
    }

    public executePrimaryAction(config: CspHeaderNotificationConfig): void {
        if (config.primaryActionId === SEE_TASKS_BUTTON) {
            this.router.navigate([OPERATOR_CONSOLE.TASKS_ABSOLUTE]);
        }
    }

}
